"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SizerDivider = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const context_1 = require("../../../styles/context");
const css_1 = require("./css");
const HIT_AREA = 17;
const HOVER_W = 5;
const ACTIVE_W = 3;
const HOVER_INSET = 2;
const ACTIVE_INSET = 8;
const blockClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    top: 0,
    bottom: 0,
    bxz: 'border-box',
    us: 'none',
    z: 2,
    op: 0,
    trs: 'opacity .15s',
    '&:focus': {
        out: 'none',
    },
    [`.${css_1.outerClass.trim()}:hover &`]: {
        op: 1,
    },
    [`.${css_1.outerClass.trim()}:has(.${css_1.contentClass.trim()}:hover) &:not(:hover)`]: {
        op: 0,
    },
    '&.dragging': {
        op: 1,
    },
});
const NO_MAX = '100vmax';
const handleClass = (0, nano_theme_1.drule)({
    pos: 'absolute',
    top: '50%',
    bxz: 'border-box',
    bdrad: '2px',
    trs: 'background .3s, width .06s, height .06s',
    w: 'var(--sz-hw, 1px)',
    h: `min(var(--sz-hmh, ${NO_MAX}), calc(100% - 2 * var(--sz-hp, 0px)))`,
    [`.${blockClass.trim()}:hover &`]: {
        w: HOVER_W + 'px',
        h: `min(calc(var(--sz-hmh, ${NO_MAX}) - ${2 * HOVER_INSET}px), calc(100% - ${2 * HOVER_INSET}px - 2 * var(--sz-hp, 0px)))`,
    },
    [`.${blockClass.trim()}:focus &`]: {
        w: ACTIVE_W + 'px',
        h: `min(calc(var(--sz-hmh, ${NO_MAX}) - ${2 * ACTIVE_INSET}px), calc(100% - ${2 * ACTIVE_INSET}px - 2 * var(--sz-hp, 0px)))`,
    },
    [`.${blockClass.trim()}.dragging &`]: {
        w: ACTIVE_W + 'px',
        h: `min(calc(var(--sz-hmh, ${NO_MAX}) - ${2 * ACTIVE_INSET}px), calc(100% - ${2 * ACTIVE_INSET}px - 2 * var(--sz-hp, 0px)))`,
    },
});
const KEY_STEP = 10;
const KEY_LARGE_STEP = 50;
const SizerDivider = ({ state, side, minWidth, disabled, handleMargin = 0, handleWidth = 1, handlePadding = 0, handleMaxHeight, }) => {
    const styles = (0, context_1.useStyles)();
    const dragging = state.dragging.use();
    const [focused, setFocused] = React.useState(false);
    const active = dragging || focused;
    const sideSign = side === 'left' ? -1 : 1;
    const content = state.content;
    const apply = React.useCallback((delta) => {
        const max = state.width.value;
        let next = content.value + delta;
        if (next < minWidth)
            next = minWidth;
        if (next > max)
            next = max;
        if (next !== content.value)
            content.next(next);
    }, [state, content, minWidth]);
    const onPointerDown = React.useCallback((e) => {
        if (disabled)
            return;
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = content.value;
        const el = e.currentTarget;
        const pointerId = e.pointerId;
        el.setPointerCapture?.(pointerId);
        state.dragging.next(true);
        const onMove = (ev) => {
            if (ev.pointerId !== pointerId)
                return;
            ev.preventDefault();
            const dx = ev.clientX - startX;
            const max = state.width.value;
            let next = startWidth + sideSign * 2 * dx;
            if (next < minWidth)
                next = minWidth;
            if (next > max)
                next = max;
            if (next !== content.value)
                content.next(next);
        };
        const cleanup = () => {
            if (el.hasPointerCapture?.(pointerId))
                el.releasePointerCapture(pointerId);
            state.dragging.next(false);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };
        const onUp = (ev) => {
            if (ev.pointerId !== pointerId)
                return;
            cleanup();
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    }, [state, content, sideSign, minWidth, disabled]);
    const onKeyDown = React.useCallback((e) => {
        if (disabled)
            return;
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')
            return;
        e.preventDefault();
        const arrow = e.key === 'ArrowLeft' ? -1 : 1;
        const step = e.shiftKey ? KEY_LARGE_STEP : KEY_STEP;
        apply(arrow * sideSign * step);
    }, [disabled, apply, sideSign]);
    const handleColor = active ? styles.col.accent(0, 5) : styles.g(0, 0.16);
    const blockStyle = {
        [side]: -(handleMargin + HIT_AREA / 2),
        width: handleMargin + HIT_AREA,
        cursor: disabled ? 'default' : 'col-resize',
        touchAction: 'none',
    };
    const handleStyle = {
        [side]: HIT_AREA / 2,
        transform: side === 'left' ? 'translate(-50%, -50%)' : 'translate(50%, -50%)',
        ['--sz-hw']: handleWidth + 'px',
        ['--sz-hp']: handlePadding + 'px',
        ...(handleMaxHeight !== undefined && { ['--sz-hmh']: handleMaxHeight + 'px' }),
    };
    return (React.createElement("div", { contentEditable: false, role: "separator", "aria-orientation": "vertical", "aria-valuenow": content.value, "aria-valuemin": minWidth, "aria-valuemax": state.width.value, tabIndex: disabled ? -1 : 0, className: blockClass + (active ? ' dragging' : ''), style: blockStyle, onPointerDown: disabled ? undefined : onPointerDown, onKeyDown: disabled ? undefined : onKeyDown, onFocus: () => setFocused(true), onBlur: () => setFocused(false), "data-sizer-side": side },
        React.createElement("div", { contentEditable: false, className: handleClass({
                bg: handleColor,
            }), style: handleStyle })));
};
exports.SizerDivider = SizerDivider;
